/* ═══════════════════════════════════════════════════════════════════════════
   FF Cart Cross-Sell — Custom element
   Handles three data sources: Recommendations API, curated collection, and
   design-tag-based (Section Rendering API). AJAX add-to-cart with full
   drawer refresh. Re-initializes automatically when Section Rendering API
   replaces the parent <cart-drawer-items> element.
   Created: Mar 9, 2026
   ═══════════════════════════════════════════════════════════════════════════ */

if (!customElements.get('ff-cart-cross-sell')) {
  class FFCartCrossSell extends HTMLElement {

    connectedCallback() {
      this.source      = this.dataset.source || 'recommendations';
      this.max         = parseInt(this.dataset.max, 10) || 2;
      this.excludeCart = this.dataset.excludeCart !== 'false';
      this.atcLabel    = this.dataset.atcLabel || 'Add';
      this.moneyFmt    = this.dataset.moneyFormat || '${{amount}}';

      /* IDs of products currently in cart (comma-separated in data attr) */
      this.cartProductIds = (this.dataset.cartProductIds || '')
        .split(',')
        .filter(Boolean);

      /* IDs of variants currently in cart */
      this.cartVariantIds = (this.dataset.cartVariantIds || '')
        .split(',')
        .filter(Boolean);

      /* Source-specific data */
      this.recProductId = this.dataset.recProductId || '';
      this.collHandle   = this.dataset.collHandle || '';
      this.designTags   = (this.dataset.designTags || '')
        .split(',')
        .filter(Boolean);

      this.container = this.querySelector('.ff-xs__cards');
      if (!this.container) return;

      /* Collection mode: cards are server-rendered. Just wire ATC. */
      if (this.source === 'collection') {
        this._wireATC();
        return;
      }

      /* Recommendations & tags: fetch client-side */
      if (this.source === 'recommendations' && this.recProductId) {
        this._fetchRecommendations();
      } else if (this.source === 'tags' && this.designTags.length) {
        this._fetchByTags();
      } else {
        /* No valid source data — hide */
        this.style.display = 'none';
      }
    }

    /* ── Recommendations API ──────────────────────────────────────────── */

    async _fetchRecommendations() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}recommendations/products.json?product_id=${this.recProductId}&limit=${this.max + this.cartProductIds.length}&intent=related`;

        const res = await fetch(url);
        if (!res.ok) return this._collapse();

        const json = await res.json();
        let products = json.products || [];

        if (this.excludeCart) {
          products = products.filter(
            (p) => !this.cartProductIds.includes(String(p.id))
          );
        }

        products = products.slice(0, this.max);
        if (!products.length) return this._collapse();

        this._renderCards(products.map((p) => this._normalizeProduct(p)));
        this._wireATC();
      } catch (e) {
        console.error('FF Cross-sell: recommendations fetch failed', e);
        this._collapse();
      }
    }

    /* ── Tag-based (Section Rendering API) ────────────────────────────── */

    async _fetchByTags() {
      const root = window.Shopify?.routes?.root || '/';
      const seen = new Set();
      let allItems = [];

      for (const tag of this.designTags) {
        try {
          const url = `${root}collections/all/${tag}?section_id=ff-cart-cross-sell-api`;
          const res = await fetch(url);
          if (!res.ok) continue;

          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const items = doc.querySelectorAll('.ff-xs__api-item');

          items.forEach((el) => {
            const handle = el.dataset.handle;
            if (seen.has(handle)) return;
            seen.add(handle);
            allItems.push({
              handle,
              title:     el.dataset.title,
              url:       el.dataset.url,
              variantId: el.dataset.variantId,
              price:     parseInt(el.dataset.price, 10),
              compare:   parseInt(el.dataset.compare, 10) || 0,
              img:       el.dataset.img || '',
              imgAlt:    el.dataset.imgAlt || '',
              imgRatio:  el.dataset.imgRatio || '1'
            });
          });
        } catch (e) {
          /* continue with next tag */
        }
      }

      if (this.excludeCart) {
        allItems = allItems.filter(
          (p) => !this.cartVariantIds.includes(String(p.variantId))
        );
      }

      allItems = allItems.slice(0, this.max);
      if (!allItems.length) return this._collapse();

      this._renderCards(allItems);
      this._wireATC();
    }

    /* ── Normalize Recommendations API product → card data ────────────── */

    _normalizeProduct(p) {
      const variant = p.variants.find((v) => v.available) || p.variants[0];
      const compare = variant.compare_at_price || 0;
      const img = p.featured_image
        ? (typeof p.featured_image === 'string' ? p.featured_image : p.featured_image)
        : (p.images && p.images[0]) || '';

      return {
        handle:    p.handle,
        title:     p.title,
        url:       p.url,
        variantId: String(variant.id),
        price:     variant.price,
        compare:   compare > variant.price ? compare : 0,
        img:       img,
        imgAlt:    p.title,
        imgRatio:  '1'
      };
    }

    /* ── Render cards into container ──────────────────────────────────── */

    _renderCards(items) {
      /* Remove loading spinner */
      const loader = this.querySelector('.ff-xs__loading');
      if (loader) loader.remove();

      this.container.innerHTML = items.map((item) => {
        const priceHtml = this._formatMoney(item.price);
        const compareHtml = item.compare > item.price
          ? `<span class="ff-xs__compare">${this._formatMoney(item.compare)}</span>`
          : '';

        const imgHtml = item.img
          ? `<div class="ff-xs__img-wrap">
               <img src="${this._imgSrc(item.img)}" alt="${this._esc(item.imgAlt)}" width="140" height="140" loading="lazy">
             </div>`
          : '';

        return `<div class="ff-xs__card">
          ${imgHtml}
          <div class="ff-xs__body">
            <p class="ff-xs__title"><a href="${item.url}" class="ff-xs__title-link"><span class="ff-xs__title-text">${this._esc(item.title)}</span></a></p>
            <div class="ff-xs__prices">
              <span class="ff-xs__price">${priceHtml}</span>
              ${compareHtml}
            </div>
          </div>
          <button type="button" class="ff-xs__atc" data-variant-id="${item.variantId}">
            ${this._esc(this.atcLabel)}
          </button>
        </div>`;
      }).join('');
    }

    /* ── Wire ATC buttons ─────────────────────────────────────────────── */

    _wireATC() {
      this.querySelectorAll('.ff-xs__atc').forEach((btn) => {
        if (btn._ffBound) return;
        btn._ffBound = true;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._addToCart(btn);
        });
      });

      /* Re-initialize button animation manager for dynamically injected elements.
         The manager adds fx fx--{type} classes based on matching selectors.
         Dispatch a DOM mutation so any MutationObserver-based re-scan picks it up,
         and also try the global init function if available. */
      this._reinitFX();
    }

    _reinitFX() {
      /* Try global re-init (set up by button-animation-manager.liquid) */
      if (typeof window.FFInitButtonAnimations === 'function') {
        window.FFInitButtonAnimations();
      }
      /* Dispatch event that other systems can listen for */
      this.dispatchEvent(new CustomEvent('ff:content-injected', { bubbles: true }));
    }

    /* ── AJAX add-to-cart ─────────────────────────────────────────────── */

    async _addToCart(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;

      try {
        const root = window.Shopify?.routes?.root || '/';

        /* 1. Add to cart */
        const addRes = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
        });

        if (!addRes.ok) throw new Error('Add to cart failed');

        /* 2. Refresh the entire drawer via Section Rendering API.
           Cross-sell is its own flex child — replace it separately
           so the server re-renders with updated cart exclusions. */
        const refreshRes = await fetch(`${root}cart?section_id=cart-drawer`);
        if (!refreshRes.ok) return;

        const html = await refreshRes.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        /* Replace cart-drawer-items (includes cross-sell inside it,
           which re-triggers connectedCallback on the custom element) */
        const newItems = doc.querySelector('cart-drawer-items');
        const oldItems = document.querySelector('cart-drawer-items');
        if (newItems && oldItems) oldItems.replaceWith(newItems);

        /* Replace footer */
        const newFooter = doc.querySelector('.cart-drawer__footer');
        const oldFooter = document.querySelector('.cart-drawer__footer');
        if (newFooter && oldFooter) {
          oldFooter.innerHTML = newFooter.innerHTML;
        }

        /* Replace progress bar */
        const newBar = doc.querySelector('.ff-cd__bar-wrap');
        const oldBar = document.querySelector('.ff-cd__bar-wrap');
        if (newBar && oldBar) oldBar.replaceWith(newBar);

        /* Replace header (item count) */
        const newHeader = doc.querySelector('.ff-cd__header');
        const oldHeader = document.querySelector('.ff-cd__header');
        if (newHeader && oldHeader) oldHeader.replaceWith(newHeader);

        /* Update cart icon bubble */
        const bubbleRes = await fetch(`${root}cart?section_id=cart-icon-bubble`);
        if (bubbleRes.ok) {
          const bubbleHtml = await bubbleRes.text();
          const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const newBubble = bubbleDoc.querySelector('.shopify-section');
          const oldBubble = document.getElementById('cart-icon-bubble');
          if (newBubble && oldBubble) {
            oldBubble.innerHTML = newBubble.innerHTML;
          }
        }

        /* Update is-empty state */
        const cartDrawer = document.querySelector('cart-drawer');
        const drawerInner = document.querySelector('.drawer__inner');
        if (cartDrawer) cartDrawer.classList.remove('is-empty');
        if (drawerInner) drawerInner.classList.remove('is-empty');

      } catch (e) {
        console.error('FF Cross-sell: add-to-cart failed', e);
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
      }
    }

    /* ── Helpers ───────────────────────────────────────────────────────── */

    _collapse() {
      this.style.display = 'none';
    }

    _esc(str) {
      const d = document.createElement('div');
      d.textContent = str || '';
      return d.innerHTML;
    }

    _formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const noTrail = amount.replace(/\.00$/, '');
      return this.moneyFmt
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', noTrail)
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
    }

    _imgSrc(src) {
      /* If it's already a CDN URL with size params, return as-is.
         Otherwise append width param for Shopify CDN images. */
      if (!src) return '';
      if (src.includes('width=') || src.includes('_300x')) return src;
      /* Recommendations API returns full URLs; add size if Shopify CDN */
      if (src.includes('cdn.shopify.com') && !src.includes('?')) {
        return src + '?width=300';
      }
      return src;
    }
  }

  customElements.define('ff-cart-cross-sell', FFCartCrossSell);
}